import {
  type AdvancedSettingsValue,
  ENTRANCE_TIME_SLOT_MODE_EVENT,
  ENTRANCE_TIME_SLOT_MODE_LIMITED,
} from '@components/advanced-settings-section';
import {
  type BasicInfoValue,
  SALES_END_TYPE_AT_EVENTS_END,
  SALES_END_TYPE_AT_EVENTS_START,
  SALES_END_TYPE_CUSTOM_DATE,
  SALES_START_TYPE_AFTER_OFFER,
  SALES_START_TYPE_CUSTOM_DATE,
  SALES_START_TYPE_NOW,
  type SalesEndType,
  type SalesStartType,
} from '@components/basic-info-section';
import {
  DISTRIBUTION_MODE_OFFLINE,
  DISTRIBUTION_MODE_ONLINE,
  type DistributionValue,
} from '@components/distribution-section';
import { type FormFieldsSectionValue } from '@components/form-fields-section';
import {
  type RegulationSettingsValue,
  SIAE_STATUS_APPROVED,
  SIAE_STATUS_EMPTY,
  SIAE_STATUS_EXCLUDED,
} from '@components/regulation-settings-section';
import { MULTIPLE_CHOICE_ADDON } from '@constants/admissions';
import { type SupportedLanguages } from '@constants/constants.type';
import { DATE_FORMAT_SHORT_TIME } from '@constants/datetime';
import { ADMISSION_STATUS_ON_SALE, type Translations } from '@domain/admission';
import { type Pass } from '@domain/passes';
import { fromBackend } from '@utils/map-date/from-backend';
import { type Moment } from 'moment-timezone';

import { type AdmissionFormValue } from '../admission-form.type';
import { ADMISSION_FORM_TYPE_PASS } from '../constants';

import { isDdpApplicable } from './from-backend';

export interface FromPassBackendContext {
  pass?: Pass | null;
  mainLanguage: SupportedLanguages;
  timezone: string;
  now: Moment;
  isEventPass: boolean;
  eventStartingTime?: number;
  eventEndingTime?: number;
  isSuperAdmin: boolean;
  canExcludeSiaeOffers: boolean;
  hasItalyRegulationContext: boolean;
  isTicketsLeftThresholdEnabled: boolean;
}

const EMPTY_TRANSLATIONS: Translations = { ca: '', de: '', en: '', es: '', fr: '', it: '', pt: '' };

const emptyFormFieldGroup = () => ({ presetRequirements: {}, customFields: [] });

const hasTranslatedContent = (pass: Pass | null | undefined, mainLanguage: SupportedLanguages): boolean =>
  [pass?.name, pass?.description].some(
    (block) =>
      block &&
      (Object.entries(block) as [SupportedLanguages, string | null][]).some(
        ([locale, text]) => locale !== mainLanguage && !!text
      )
  );

const derivePassSalesStartType = (pass: Pass | null | undefined): SalesStartType => {
  if (!pass) return SALES_START_TYPE_NOW;
  if (pass.offerReleasedBefore) return SALES_START_TYPE_AFTER_OFFER;
  return SALES_START_TYPE_CUSTOM_DATE;
};

const derivePassSalesEndType = (
  pass: Pass | null | undefined,
  eventStartingTime: number,
  eventEndingTime: number
): SalesEndType => {
  if (!pass || pass.salesTime.closingTime === eventStartingTime) return SALES_END_TYPE_AT_EVENTS_START;
  if (pass.salesTime.closingTime === eventEndingTime) return SALES_END_TYPE_AT_EVENTS_END;
  return SALES_END_TYPE_CUSTOM_DATE;
};

const buildBasicInfo = ({
  pass,
  timezone,
  now,
  isEventPass,
  eventStartingTime,
  eventEndingTime,
  hasItalyRegulationContext,
  mainLanguage,
}: FromPassBackendContext): BasicInfoValue => {
  const validFrom = pass?.passValidity.validFrom ? fromBackend(pass.passValidity.validFrom, timezone) : null;
  const validUntil = pass?.passValidity.validUntil ? fromBackend(pass.passValidity.validUntil, timezone) : null;

  const value: BasicInfoValue = {
    price: pass?.price.amount.toString() ?? '',
    quantity: pass?.quantity == null ? '' : pass.quantity.toString(),
    itemsPerTransaction: {
      min: pass?.itemsPerTransaction.min.toString() ?? '',
      max: pass?.itemsPerTransaction.max.toString() ?? '',
    },
    title: pass?.name ?? { ...EMPTY_TRANSLATIONS },
    description: pass?.description ?? { ...EMPTY_TRANSLATIONS },
    translationsEnabled: hasTranslatedContent(pass, mainLanguage),
    passValidity: {
      start: validFrom,
      end: validUntil,
      days: pass?.passValidity.passDayValidity ?? [],
    },
  };

  if (!isEventPass) {
    value.startAfter = { passId: pass?.offerReleasedBefore ?? null, displayName: '' };
  }

  if (isDdpApplicable(ADMISSION_FORM_TYPE_PASS, hasItalyRegulationContext)) {
    value.ddp = pass?.settings.italyRegulation?.dirittoPrevendita?.toString() ?? '';
  }

  if (isEventPass && eventStartingTime !== undefined && eventEndingTime !== undefined) {
    const start = pass?.salesTime.openingTime ? fromBackend(pass.salesTime.openingTime, timezone) : now;
    const end = fromBackend(pass ? pass.salesTime.closingTime : eventStartingTime, timezone);
    value.salesWindow = {
      startType: derivePassSalesStartType(pass),
      startDate: start,
      startTime: start.format(DATE_FORMAT_SHORT_TIME),
      endType: derivePassSalesEndType(pass, eventStartingTime, eventEndingTime),
      endDate: end,
      endTime: end.format(DATE_FORMAT_SHORT_TIME),
    };
  }

  return value;
};

const buildDistribution = (pass?: Pass | null): DistributionValue => ({
  mode:
    pass?.settings.distribution ?? (pass?.settings.isPrivate ? DISTRIBUTION_MODE_OFFLINE : DISTRIBUTION_MODE_ONLINE),
  accessCode: { enabled: !!pass?.accessCode, code: pass?.accessCode ?? '' },
});

const buildAdvanced = ({
  pass,
  timezone,
  isTicketsLeftThresholdEnabled,
}: FromPassBackendContext): AdvancedSettingsValue => {
  const validFrom = pass?.checkInTime?.validFrom ? fromBackend(pass.checkInTime.validFrom, timezone) : null;
  const validUntil = pass?.checkInTime?.validUntil ? fromBackend(pass.checkInTime.validUntil, timezone) : null;

  return {
    entranceTimeSlot: {
      mode: validFrom !== null || validUntil !== null ? ENTRANCE_TIME_SLOT_MODE_LIMITED : ENTRANCE_TIME_SLOT_MODE_EVENT,
      validFrom,
      validFromTime: validFrom?.format(DATE_FORMAT_SHORT_TIME) ?? '',
      validUntil,
      validUntilTime: validUntil?.format(DATE_FORMAT_SHORT_TIME) ?? '',
    },
    ...(isTicketsLeftThresholdEnabled && {
      lowStockThreshold: {
        enabled: pass?.ticketsLeftThreshold != null,
        quantity: pass?.ticketsLeftThreshold?.toString() ?? '',
      },
    }),
  };
};

const buildFormFields = (pass?: Pass | null): FormFieldsSectionValue => ({
  isNominative: pass?.settings.isNominative ?? true,
  mainHolder: emptyFormFieldGroup(),
  ticketHolder: emptyFormFieldGroup(),
});

const buildRegulation = ({
  pass,
  isSuperAdmin,
  canExcludeSiaeOffers,
  hasItalyRegulationContext,
}: FromPassBackendContext): RegulationSettingsValue => {
  if (!hasItalyRegulationContext) return {};

  const italyRegulation = pass?.settings.italyRegulation;
  const value: RegulationSettingsValue = {};

  if (isSuperAdmin) {
    value.allows18App = italyRegulation?.allows18App ?? false;
    if (italyRegulation?.isSiaeApproved) {
      value.siaeStatus = SIAE_STATUS_APPROVED;
    } else if (italyRegulation?.isSiaeExcluded) {
      value.siaeStatus = SIAE_STATUS_EXCLUDED;
    } else {
      value.siaeStatus = SIAE_STATUS_EMPTY;
    }
    value.abbonamentoId = italyRegulation?.abbonamentoId ?? null;
  }

  if (canExcludeSiaeOffers && !isSuperAdmin) {
    value.excludeFromC1 = italyRegulation?.isSiaeExcluded ?? false;
  }

  return value;
};

export const toPassFormValue = (ctx: FromPassBackendContext): AdmissionFormValue => ({
  status: ctx.pass?.status ?? ADMISSION_STATUS_ON_SALE,
  basicInfo: buildBasicInfo(ctx),
  distribution: buildDistribution(ctx.pass),
  addOns: { selectedAddOnIds: [], hasMandatoryAddOns: false, addOnChoiceMode: MULTIPLE_CHOICE_ADDON },
  advanced: buildAdvanced(ctx),
  formFields: buildFormFields(ctx.pass),
  appearance: { color: ctx.pass?.color ?? '' },
  vatRate: { vatRate: ctx.pass?.vatRate ?? null },
  regulation: buildRegulation(ctx),
});
